import React from 'react';
import PropTypes from 'prop-types';
import theme from '@nubank/nuds-web/styles/theme';
import Head from 'next/head';

import compose from '../../scripts/utils/functional/compose';
import { addCMSQueryParametersToSrcSet } from '../../utils/cms/images/addCMSQueryParameters';
import { resolveCMSImageSrcSet } from '../../utils/cms/images/resolveCMSImage';
import { requireImage } from '../../utils/requireImage';

const SIZE_ORDER = ['xs', 'sm', 'md', 'lg', 'xl'];
const { breakpoints } = theme;

const isExternal = source => !!source.match(/https?:\/\//);
const getImage = source => (isExternal(source) ? source : requireImage(source, 'webp'));

const ImagePreload = ({ src, srcSet }) => {
  if (src) {
    return (
      <Head>
        <link
          rel="preload"
          as="image"
          href={getImage(src, 'webp')}
        />
      </Head>
    );
  }

  const sizes = SIZE_ORDER.filter(size => !!srcSet[size]);

  const medias = sizes.reduce((acc, breakpoint, index) => {
    const rules = [];
    const next = breakpoints[sizes[index + 1]];
    rules.push(`(min-width:${breakpoints[breakpoint]}px)`);
    if (next) {
      rules.push(`(max-width:${next - 0.1}px)`);
    }
    acc[breakpoint] = rules.join(' and ');
    return acc;
  }, {});

  const formattedImages = sizes.reduce((acc, breakpoint) => {
    const image = srcSet[breakpoint];
    if (typeof image === 'string') {
      acc.push({ href: getImage(image, 'webp'), media: medias[breakpoint] });
    } else {
      const sources = Object.entries(image).map(([density, source]) => (
        `${getImage(source, 'webp')} ${density}`
      ));
      acc.push({ imagesrcset: sources.join(','), media: medias[breakpoint] });
    }
    return acc;
  }, []);

  return (
    <Head>
      {formattedImages.map(image => (
        <link
          key={image.media}
          rel="preload"
          as="image"
          {...image}
        />
      ))}
    </Head>
  );
};

export default ImagePreload;

export const prepareImagePreload = compose(
  resolveCMSImageSrcSet,
  addCMSQueryParametersToSrcSet,
);

const DPRPropTypes = PropTypes.shape({
  '1x': PropTypes.string.isRequired,
  '2x': PropTypes.string,
  '3x': PropTypes.string,
});

ImagePreload.defaultProps = {
  src: undefined,
  srcSet: undefined,
};

ImagePreload.propTypes = {
  src: PropTypes.string,
  srcSet: PropTypes.shape({
    /* eslint-disable react/sort-prop-types */
    xl: PropTypes.oneOfType([PropTypes.string, DPRPropTypes]),
    lg: PropTypes.oneOfType([PropTypes.string, DPRPropTypes]),
    md: PropTypes.oneOfType([PropTypes.string, DPRPropTypes]),
    sm: PropTypes.oneOfType([PropTypes.string, DPRPropTypes]),
    xs: PropTypes.oneOfType([PropTypes.string, DPRPropTypes]),
    /* eslint-disable react/sort-prop-types */
  }),
};
